(() => {
  const mobileMedia = window.matchMedia('(max-width: 989px)');
  const managed = {
    smile: new Set(),
    smileFull: new Set(),
    smilePanels: new Set(),
    chat: new Set(),
    controls: new Set()
  };
  const chatRetries = new WeakMap();
  const chatRetryTimers = new WeakMap();
  const smileSelector = '#smile-ui-lite-container';
  const smileFullSelector = '#smile-ui-container .smile-launcher-frame-container';
  const smilePanelSelector = '#smile-ui-container .smile-panel-frame-container';
  const chatSelector = [
    'shopify-agent',
    'shopify-chat',
    '#shopify-chat',
    '.shopify-chat',
    'iframe[src*="shopify-agent"]',
    'iframe[src*="inbox.shopify"]'
  ].join(',');
  const gridSelector = 'main .product-grid, main #product-grid, main .dt-product-card-grid';
  const controlSelector = '.dt-product-card__purchase, .dt-product-card__sold-out, .product-form__buttons, .cart__ctas, .cart-drawer__footer';
  const discoverySelector = [smileSelector, smileFullSelector, smilePanelSelector, chatSelector, controlSelector].join(',');
  const layoutSelector = `${gridSelector}, main .page-width`;
  const paymentOptionsId = 'more-payment-options-link';

  function dedupePaymentOptionIds() {
    const links = [...document.querySelectorAll(`[id="${paymentOptionsId}"]`)];
    if (links.length < 2) return false;

    const usedIds = new Set([...document.querySelectorAll('[id]')].map((element) => element.id));
    let suffix = 2;
    links.slice(1).forEach((link) => {
      while (usedIds.has(`${paymentOptionsId}-${suffix}`)) suffix += 1;
      const uniqueId = `${paymentOptionsId}-${suffix}`;
      link.id = uniqueId;
      usedIds.add(uniqueId);
      suffix += 1;
    });
    return true;
  }

  function visible(element) {
    if (!element || !element.isConnected || element.getClientRects().length === 0) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function floating(element) {
    if (!element || element === document.body || element === document.documentElement) return false;
    const position = getComputedStyle(element).position;
    return position === 'fixed' || position === 'absolute' || position === 'sticky';
  }

  function smileContainer(element) {
    if (!element) return null;
    if (element.matches?.('#smile-ui-lite-container')) return element;
    return element.closest?.('#smile-ui-lite-container') || null;
  }

  function chatContainer(element) {
    const host = element.closest?.('shopify-chat, shopify-agent');
    if (host) return host;
    let current = element;
    let best = element;
    for (let depth = 0; current && depth < 8; depth += 1) {
      const identity = `${current.id || ''} ${current.className || ''}`.toLowerCase();
      if (identity.includes('shopify-chat') || identity.includes('shopify-agent') || identity.includes('inbox') || floating(current)) {
        best = current;
      }
      current = current.parentElement;
    }
    return best;
  }

  function addToSet(set, value) {
    if (!value || set.has(value)) return false;
    set.add(value);
    return true;
  }

  function discover(root = document) {
    let changed = false;
    const elements = [];
    if (root instanceof Element && root.matches(discoverySelector)) elements.push(root);
    if (root.querySelectorAll) elements.push(...root.querySelectorAll(discoverySelector));
    elements.forEach((element) => {
      if (element.matches(smileSelector)) changed = addToSet(managed.smile, smileContainer(element)) || changed;
      if (element.matches(smileFullSelector)) changed = addToSet(managed.smileFull, element) || changed;
      if (element.matches(smilePanelSelector)) changed = addToSet(managed.smilePanels, element) || changed;
      if (element.matches(chatSelector)) changed = addToSet(managed.chat, chatContainer(element)) || changed;
      if (element.matches(controlSelector)) changed = addToSet(managed.controls, element) || changed;
    });
    return changed;
  }

  function removeDetached() {
    Object.values(managed).forEach((elements) => {
      elements.forEach((element) => {
        if (!element?.isConnected) elements.delete(element);
      });
    });
  }

  function contentBounds() {
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    let elements = [...document.querySelectorAll(gridSelector)].filter(visible);
    if (!elements.length) elements = [...document.querySelectorAll('main .page-width')].filter(visible);
    const rects = elements
      .map((element) => element.getBoundingClientRect())
      .filter((rect) => rect.width >= 200)
      .sort((first, second) => second.width - first.width);
    if (!rects.length) return null;
    return { left: Math.max(0, rects[0].left), right: Math.min(viewportWidth, rects[0].right) };
  }

  function setRootVariable(name, value) {
    const root = document.documentElement;
    if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
  }

  function updateLayout() {
    if (mobileMedia.matches) {
      setRootVariable('--dt-launcher-size', '60px');
      setRootVariable('--dt-smile-scale', '1');
      setRootVariable('--dt-smile-left', '16px');
      setRootVariable('--dt-smile-panel-left', '0px');
      setRootVariable('--dt-smile-panel-width', '100vw');
      setRootVariable('--dt-chat-right', '16px');
      setRootVariable('--dt-floating-bottom', 'calc(16px + env(safe-area-inset-bottom, 0px))');
      return;
    }

    const bounds = contentBounds();
    if (!bounds) {
      setRootVariable('--dt-launcher-size', '60px');
      setRootVariable('--dt-smile-scale', '1');
      setRootVariable('--dt-smile-left', '20px');
      setRootVariable('--dt-smile-panel-left', '92px');
      setRootVariable('--dt-smile-panel-width', '360px');
      setRootVariable('--dt-chat-right', '20px');
      setRootVariable('--dt-floating-bottom', '20px');
      return;
    }

    const leftGutter = bounds.left;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const rightGutter = Math.max(0, viewportWidth - bounds.right);
    const minimumGutter = Math.min(leftGutter, rightGutter);
    const size = minimumGutter >= 72 ? 60 : minimumGutter >= 50 ? 48 : Math.max(32, Math.floor(minimumGutter));
    const preferredGap = size === 60 ? 12 : 2;
    const leftGap = Math.max(0, Math.min(preferredGap, leftGutter - size));
    const rightGap = Math.max(0, Math.min(preferredGap, rightGutter - size));
    const smileLeft = Math.max(0, Math.round(bounds.left - size - leftGap));
    const chatRight = Math.max(0, Math.round(viewportWidth - bounds.right - size - rightGap));
    const preferredPanelWidth = 360;
    const minimumPanelWidth = 240;
    const panelGap = 12;
    const viewportPadding = 12;
    const leftAvailable = smileLeft - panelGap - viewportPadding;
    const opensLeft = leftAvailable >= minimumPanelWidth;
    const panelWidth = opensLeft
      ? Math.min(preferredPanelWidth, leftAvailable)
      : Math.min(preferredPanelWidth, viewportWidth - (2 * viewportPadding));
    const panelLeft = opensLeft
      ? smileLeft - panelWidth - panelGap
      : Math.max(
        viewportPadding,
        Math.min(viewportWidth - panelWidth - viewportPadding, smileLeft + size + panelGap)
      );

    setRootVariable('--dt-launcher-size', `${size}px`);
    setRootVariable('--dt-smile-scale', String(size / 60));
    setRootVariable('--dt-smile-left', `${smileLeft}px`);
    setRootVariable('--dt-smile-panel-left', `${Math.round(panelLeft)}px`);
    setRootVariable('--dt-smile-panel-width', `${Math.round(panelWidth)}px`);
    setRootVariable('--dt-chat-right', `${chatRight}px`);
    setRootVariable('--dt-floating-bottom', '20px');
  }

  function rootVariable(name, fallback) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  }

  function setImportant(element, property, value) {
    if (!element) return;
    if (element.style.getPropertyValue(property) === value && element.style.getPropertyPriority(property) === 'important') return;
    element.style.setProperty(property, value, 'important');
  }

  function styleSmile(element) {
    if (!element?.matches?.('#smile-ui-lite-container')) return;
    const size = rootVariable('--dt-launcher-size', '60px');
    const scale = rootVariable('--dt-smile-scale', '1');
    const left = rootVariable('--dt-smile-left', '20px');
    setImportant(element, 'position', 'fixed');
    setImportant(element, 'left', '0px');
    setImportant(element, 'right', 'auto');
    setImportant(element, 'bottom', rootVariable('--dt-floating-bottom', '20px'));
    setImportant(element, 'width', size);
    setImportant(element, 'min-width', '0px');
    setImportant(element, 'max-width', size);
    setImportant(element, 'height', size);
    setImportant(element, 'box-sizing', 'border-box');
    setImportant(element, 'transform', `translate3d(${left}, 0, 0)`);
    setImportant(element, 'transition', 'none');

    const frameContainer = element.querySelector?.('#smile-ui-lite-launcher-frame-container');
    setImportant(frameContainer, 'position', 'absolute');
    setImportant(frameContainer, 'left', '0px');
    setImportant(frameContainer, 'right', 'auto');
    setImportant(frameContainer, 'bottom', '0px');
    setImportant(frameContainer, 'width', size);
    setImportant(frameContainer, 'min-width', '0px');
    setImportant(frameContainer, 'max-width', size);
    setImportant(frameContainer, 'height', size);
    setImportant(frameContainer, 'overflow', 'visible');
    setImportant(frameContainer, 'transform', 'none');
    setImportant(frameContainer, 'transition', 'none');

    const frame = element.querySelector?.('#smile-lite-launcher-frame');
    setImportant(frame, 'width', '60px');
    setImportant(frame, 'min-width', '60px');
    setImportant(frame, 'max-width', '60px');
    setImportant(frame, 'height', '60px');
    setImportant(frame, 'transform', `scale(${scale})`);
    setImportant(frame, 'transform-origin', 'left bottom');
    element.classList.add('dt-managed-smile-widget');
  }

  function styleFullSmileLauncher(element) {
    if (!element?.matches?.('.smile-launcher-frame-container')) return;
    const scale = rootVariable('--dt-smile-scale', '1');
    setImportant(element, 'position', 'fixed');
    setImportant(element, 'left', rootVariable('--dt-smile-left', '20px'));
    setImportant(element, 'right', 'auto');
    setImportant(element, 'bottom', rootVariable('--dt-floating-bottom', '20px'));
    setImportant(element, 'width', '60px');
    setImportant(element, 'min-width', '60px');
    setImportant(element, 'max-width', '60px');
    setImportant(element, 'height', '60px');
    setImportant(element, 'transform', `scale(${scale})`);
    setImportant(element, 'transform-origin', 'left bottom');
    setImportant(element, 'transition', 'none');
    element.classList.add('dt-managed-smile-full-launcher');
  }

  function styleSmilePanel(element) {
    if (!element?.matches?.('.smile-panel-frame-container')) return;
    element.classList.add('dt-managed-smile-panel');
    if (mobileMedia.matches) return;
    setImportant(element, 'left', rootVariable('--dt-smile-panel-left', '12px'));
    setImportant(element, 'right', 'auto');
    setImportant(element, 'width', rootVariable('--dt-smile-panel-width', '360px'));
  }

  function chatParts(element) {
    if (!element || !['SHOPIFY-CHAT', 'SHOPIFY-AGENT'].includes(element.tagName)) return {};
    const root = element.shadowRoot;
    if (!root) return {};
    return {
      root,
      nudge: root.querySelector('.activator-nudge--right, .activator-nudge'),
      button: root.querySelector('[part="activator"], .activator'),
      label: root.querySelector('.activator__label'),
      icon: root.querySelector('.activator__icon'),
      svg: root.querySelector('.activator__icon svg')
    };
  }

  function queueChatRetry(element) {
    if (!element?.isConnected || chatRetryTimers.has(element)) return;
    const attempts = chatRetries.get(element) || 0;
    if (attempts >= 24) return;
    const timer = setTimeout(() => {
      chatRetryTimers.delete(element);
      chatRetries.set(element, attempts + 1);
      styleChat(element);
      schedule(false);
    }, 250);
    chatRetryTimers.set(element, timer);
  }

  function styleChat(element) {
    const parts = chatParts(element);
    if (!parts.root || !parts.button) {
      queueChatRetry(element);
      return;
    }
    chatRetries.delete(element);

    const size = rootVariable('--dt-launcher-size', '60px');
    element.classList.add('dt-managed-chat-widget');
    setImportant(parts.nudge, 'right', rootVariable('--dt-chat-right', '20px'));
    setImportant(parts.nudge, 'bottom', rootVariable('--dt-floating-bottom', '20px'));
    setImportant(parts.button, 'display', 'grid');
    setImportant(parts.button, 'width', size);
    setImportant(parts.button, 'min-width', size);
    setImportant(parts.button, 'height', size);
    setImportant(parts.button, 'padding', '0');
    setImportant(parts.button, 'border-radius', '999px');
    setImportant(parts.button, 'background', '#5a49d2');
    setImportant(parts.button, 'color', '#ffffff');
    setImportant(parts.button, 'grid-template-columns', '1fr');
    setImportant(parts.button, 'place-items', 'center');
    setImportant(parts.button, 'box-shadow', '0 4px 14px rgba(28, 34, 40, 0.2)');
    setImportant(parts.label, 'display', 'none');
    const iconSize = `${Math.round(Number.parseFloat(size) * 0.4)}px`;
    [parts.icon, parts.svg].forEach((icon) => {
      setImportant(icon, 'width', iconSize);
      setImportant(icon, 'height', iconSize);
      setImportant(icon, 'color', '#ffffff');
    });
  }

  function menuOpen() {
    return Boolean(
      document.querySelector('#Details-menu-drawer-container[open]') ||
      document.querySelector('header-drawer details[open]') ||
      document.body.classList.contains('overflow-hidden-tablet')
    );
  }

  function overlaps(first, second, gap = 8) {
    return !(
      first.right + gap < second.left || first.left - gap > second.right ||
      first.bottom + gap < second.top || first.top - gap > second.bottom
    );
  }

  function coversPurchaseControl(rect) {
    if (!rect?.width || !rect?.height) return false;
    for (const control of managed.controls) {
      if (visible(control) && overlaps(rect, control.getBoundingClientRect())) return true;
    }
    return false;
  }

  function apply(recalculateLayout) {
    const isMobile = mobileMedia.matches;
    const open = isMobile && menuOpen();
    document.body.classList.toggle('dt-mobile-menu-active', open);
    removeDetached();
    if (recalculateLayout) updateLayout();

    managed.smile.forEach((element) => {
      styleSmile(element);
      element.classList.toggle('dt-widget-control-collision', isMobile && !open && coversPurchaseControl(element.getBoundingClientRect()));
    });
    managed.smileFull.forEach((element) => {
      styleFullSmileLauncher(element);
      const panelOpen = element.classList.contains('smile-launcher-open');
      element.classList.toggle(
        'dt-widget-control-collision',
        isMobile && !open && !panelOpen && coversPurchaseControl(element.getBoundingClientRect())
      );
    });
    managed.smilePanels.forEach(styleSmilePanel);
    managed.chat.forEach((element) => {
      styleChat(element);
      const button = chatParts(element).button;
      element.classList.toggle('dt-widget-control-collision', isMobile && !open && coversPurchaseControl(button?.getBoundingClientRect()));
    });
  }

  let queued = false;
  let layoutPending = true;
  function schedule(recalculateLayout = false) {
    layoutPending = layoutPending || recalculateLayout;
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      const shouldRecalculate = layoutPending;
      queued = false;
      layoutPending = false;
      apply(shouldRecalculate);
    });
  }

  function initialise() {
    discover(document);
    dedupePaymentOptionIds();
    schedule(true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
  window.addEventListener('load', () => schedule(true), { once: true });
  window.addEventListener('resize', () => schedule(true), { passive: true });
  window.addEventListener('scroll', () => {
    if (mobileMedia.matches && (managed.smile.size || managed.chat.size)) schedule(false);
  }, { passive: true });
  document.addEventListener('toggle', () => schedule(false), true);
  if (mobileMedia.addEventListener) mobileMedia.addEventListener('change', () => schedule(true));
  else mobileMedia.addListener(() => schedule(true));

  const pendingDiscoveryNodes = new Set();
  let discoveryQueued = false;
  function flushDiscovery() {
    discoveryQueued = false;
    const nodes = [...pendingDiscoveryNodes].filter((node) => node.isConnected);
    pendingDiscoveryNodes.clear();
    if (!nodes.length) return;

    let relevant = false;
    let affectsLayout = false;
    let mayContainPaymentOptions = false;
    if (nodes.length > 16) relevant = discover(document);
    nodes.forEach((node) => {
      if (nodes.length <= 16) relevant = discover(node) || relevant;
      affectsLayout = affectsLayout || node.matches(layoutSelector) || Boolean(node.querySelector?.(layoutSelector));
      mayContainPaymentOptions = mayContainPaymentOptions ||
        node.id === paymentOptionsId ||
        Boolean(node.querySelector?.(`[id="${paymentOptionsId}"]`));
    });
    if (mayContainPaymentOptions) dedupePaymentOptionIds();
    if (relevant || affectsLayout) schedule(affectsLayout);
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) pendingDiscoveryNodes.add(node);
    }));
    if (!pendingDiscoveryNodes.size || discoveryQueued) return;
    discoveryQueued = true;
    requestAnimationFrame(flushDiscovery);
  }).observe(document.body, { childList: true, subtree: true });
})();
